import { Injectable, inject } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { catchError, map, Observable, of } from 'rxjs';
import { ENVIRONMENT } from '../../environments/environment';
import { TokenStorageService } from '../core/services/token-storage.service';
import { VenueContextService } from '../core/services/venue-context.service';

export interface RemainingSubscriptionDays {
  RoleId: number;
  UserId: number;
  BusinessId: number;
}

export interface PayToAgreementRequest {
  subscriptionId: number;
  payerBsb: string;
  payerAccountNumber: string;
  payerName: string;
}

export interface PayToAgreementResponse {
  success: boolean;
  message: string;
  payToAgreementId?: number;
  agreementUrl?: string;
  providerAgreementId?: string;
}

export interface SubscriptionCreateRequest {
  planId: number;
  planPriceId: number;
}

export interface SubscriptionCreateResponse {
  success: boolean;
  message: string;
  subscriptionId?: number;
}
export interface SubscriptionPlan {
  RoleId: number;
  Currency: string;
  // UserId: number;
  BusinessId: number;
}
export interface CheckoutStartRequest {
  subscriberType: string;
  businessId?: number;
  userId: string;
  roleId: number;
  planCode: string;
  currencyCode: string;
  paymentMethod: string;
  correlationId?: string;
  planId?: number;
  couponCode?: string;
}

export interface CheckoutStartResponse {
  correlationId: string;
  subscriptionId: number;
  invoiceId: number;
  invoiceNumber: string;
  paymentId: number;
  payToAgreementId?: number;
  amount: number;
  currencyCode: string;
  paymentMethod: string;
}
export interface PostPayEligibilityResponse {
  businessId: number;
  eligible: boolean;
  terms: 'NET_7' | 'NET_14' | 'NET_30';
  approvedBy?: number;
  approvedUtc?: string;
}

export interface ActivatePostPayRequest {
  businessId: number;
  planId: number;
  planCode: string;
  currencyCode: string;
  correlationId?: string;
}

export interface ActivatePostPayResponse {
  subscriptionId: number;
  invoiceId: number;
  invoiceNumber: string;
  paymentId: number;
  amount: number;
  currencyCode: string;
  paymentMethod: string;
  terms: string;
  dueDateUtc: string;
  subscriptionStatus: string;
}
// export interface ApiResponse<T> {
//   success: boolean;
//   message: string;
//   data?: T;
// }
export interface ApiResponse<T> {
  pageSize: number;
  totalPages: number;
  totalRecords: number;
  success: boolean;
  message: string;
  data?: T;
}
export interface CreateCardSessionRequest {
  paymentId: number;
  correlationId: string;
  billingAddress: BillingAddress;
  SaveCard: boolean;
  SubscriberType: string;
  BusinessId: number;
  UserId: number;
}

export interface CreateCardSessionResponse {
  paymentId: number;
  correlationId: string;
  clientToken: string;
  clientTokenExpirationDate?: string;
  message: string;
  securityToken: string;
}
export interface BillingAddress {
  firstName: string;
  email: string;
  lastName: string;
  street: string[];
  suburb: string;
  state: string;
  postalCode: string;
  countryCode: string;
}
export interface IsActiveSubscriptionRequest {
  roleId: number;
  userId: number;
  businessId: number;
}
// export interface IsActiveSubscriptionResponse {
//   success: boolean;
//   message: string;
//   data?: boolean;
// }
export interface IsActiveSubscriptionResponse {
  success: boolean;
  message: string;
  data?: Array<{
    SubscriptionId: number;
    SubscriberType: string;
    UserId: number;
    BusinessId: number;
    RoleId: number;
    PlanId: number;
    PlanPriceId: number;
    Status: string;
    CurrentPeriodStartUtc: string;
    CurrentPeriodEndUtc: string;
    isActiveSubscription: number; // 1 or 0
  }>;
}
export interface PaymentSuccessRequest {
  UserId: number;
  RoleId: number;
  MonoovaId: string;
  MonoovaOrderId: string;
  CorrelationId: string;
  CouponCode?: string;
  CardToken?: CardTokenFromPayment | null;

  ClientTransactionUniqueReference?: string;   
  ClientPaymentTokenUniqueReference?: string; 
}

export interface CardTokenFromPayment {
  tokenReference: string;
  cardBrand: string;
  cardLastFour: string;
  expiryMonth?: number;
  expiryYear?: number;
  cardholderName?: string;
}

// ============ Transaction Models ============
export interface MonoovaTransactionResponse {
  traceId?: string;
  creditCardTransactionDetails?: CreditCardTransactionDetails;
}

export interface CreditCardTransactionDetails {
  clientTransactionUniqueReference?: string;
  transactionId?: string;
  customerId?: string;
  status?: string;
  statusReasonCode?: string;
  statusReasonDescription?: string;
  amount?: MonoovaAmount;
  surchargeAmount?: MonoovaAmount;
  transactionType?: string;
  creationDateTime?: string;
  settlementDateTime?: string;
  refundStatus?: string;
  cardDetails?: TransactionCardDetails;
}

export interface MonoovaAmount {
  currencyAmount: number;
  currencyCode?: string;
}

export interface TransactionCardDetails {
  cardScheme?: string;
  cardNumberLast4Digits?: string;
  expiryMonth?: number;
  expiryYear?: number;
}

export interface MonoovaTransactionListResponse {
  traceId?: string;
  creditCardTransactionDetails?: CreditCardTransactionDetails[];
}

// ============ Refund Models ============
export interface RefundRequest {
  originalTransactionReference: string;
  amount?: number;
  reason?: string;
  requestedBy?: string;
}

export interface RefundResponse {
  uniqueRequestId?: string;
  refundTransactionReference?: string;
  status?: string;
}

export interface MonoovaAsyncStatusResponse {
  traceId?: string;
  uniqueRequestId?: string;
  clientTransactionUniqueReference?: string;
  status?: string; // Processing, Completed, Failed, Expired
  entityType?: string;
  actionType?: string;
  location?: string;
  errorCode?: string;
  errorMessage?: string;
}

// ============ Payment Token Models ============
export interface PaymentMethodTokenDetail {
  clientPaymentTokenUniqueReference?: string;
  paymentMethodTokenId?: string;
  customerId?: string;
  cardScheme?: string;
  cardNumberLast4Digits?: string;
  expiryMonth?: number;
  expiryYear?: number;
  cardHolderName?: string;
  status?: string; // Active, Paused, Deleted
  creationDateTime?: string;
}

export interface MonoovaTokenDetailsResponse {
  traceId?: string;
  paymentMethodTokenDetails?: PaymentMethodTokenDetail[];
}

export interface UpdateTokenStatusRequest {
  status: 'Active' | 'Paused' | 'Deleted';
}

export interface SubscriptionBillingRequest {
  RoleId: number;
  UserId: number;
  BusinessId: number;
}

export interface SubscriptionBillingResponse {
  planName: string;
  currentPeriodStartUtc: string;
  currentPeriodEndUtc: string;
  amountDue: number;
  status: string;
}

export interface SubscriptionBillingResponse {
  invoiceNumber: string;
  planName: string;
  currentPeriodStartUtc: string;
  currentPeriodEndUtc: string;
  amountDue: number;
  status: string;
}
export interface ApplyDiscountCodeResponse {
  isValid: boolean;
  subTotal: number;
  gst: number;
  discount: number;
  totalDue: number;
  message?: string;
  Message?: string;
  planCode: string;
  planId: number;
  subscriptionEndTime: string;
}
export interface ApplyDiscountCodeRequest {
  UserId: number;
  BusinessId: number;
  RoleId: number;
  PlanId: number;
  CurrencyCode: string;
  CouponCode: string;
  SubTotal: number;
}
export interface InsertFreeTrialRequest {
  UserId: number;
  RoleId: number;
  BusinessId: number;
  SubscriberType: string;
  CurrencyCode: string;
  CouponCode: string;
}

export interface InsertFreeTrialResponse {
  SubscriptionId: number;
  TrialEndsUtc: string;
}
export interface SaveBillingAddressRequest {
  UserId: number;
  BillingEmail: string;
  FirstName: string;
  LastName: string;
  CompanyName: string;
  AddressLine1: string;
  AddressLine2: string;
  StreetName: string;
  City: string;
  State: string;
  PostalCode: string;
  Country: string;
  Phone: string;
  IsDefault: boolean;
  CreatedBy: number;
  BusinessId: number;
}
export interface BusinessAddressResponse {
  address_BuildingName?: string;
  address_OptionalDetails?: string;
  address_StreetName?: string;
  contact_City?: string;
  contact_Postcode?: string;
  contact_Country?: string;
  statecode?: string;
  firstName?: string;        
  lastName?: string;        
  billingEmail?: string;     
  companyName?: string;     
  phone?: string;       
}
export interface ShowcaseCreatorCompletedResponse {
  correlationId: string;
  showcasePaymentId: number;
  showcaseId: number;
  businessId: number;
  collaborationRequestId: number;
  creatorUserId: number;
  invoiceId: number;
  invoiceNumber: string;
  paymentId: number;
  cardTokenId?: number;
  agreedAmount: number;
  platformFeeAmount: number;
  totalChargedAmount: number;
  currencyCode: string;
  paymentMethod: string;
}

//update card details token
export interface CardUpdateStartRequest {
  businessId: number;
  userId: number;
}
 
export interface CardUpdateStartResponse {
  paymentId: number;
  invoiceId: number;
  invoiceNumber: string;
  correlationId: string;
  amount: number;
  currencyCode: string;
  paymentMethod: string;
  cardLastFour?: string;
  cardNumberLast4Digits?: string;
  cardScheme?: string;
}
 
export interface CardUpdateCompleteRequest {
  businessId: number;
  userId: number;
  clientPaymentTokenUniqueReference: string;
}

@Injectable({
  providedIn: 'root',
})
export class MonoovaService {
  private http = inject(HttpClient);
  private tokenStorage = inject(TokenStorageService);
  private venueContextService = inject(VenueContextService);
  private apiUrl: string = ENVIRONMENT.baseUrls.baseAPIUrl;
  private isActiveSubscriptionFlag: string | null = null;

  getSubscriptionBilling(
    request: SubscriptionBillingRequest,
  ): Observable<ApiResponse<SubscriptionBillingResponse[]>> {
    return this.http.post<ApiResponse<SubscriptionBillingResponse[]>>(
      `${this.apiUrl}Payment/get_subscription_billing`,
      request,
    );
  }
  ApplyDiscountCode(
    request: ApplyDiscountCodeRequest,
  ): Observable<ApiResponse<ApplyDiscountCodeResponse>> {
    return this.http.post<ApiResponse<ApplyDiscountCodeResponse>>(
      `${this.apiUrl}Payment/apply_discount_code`,
      request,
    );
  }

  insertFreeTrial(
    request: InsertFreeTrialRequest,
  ): Observable<ApiResponse<InsertFreeTrialResponse>> {
    return this.http.post<ApiResponse<InsertFreeTrialResponse>>(
      `${this.apiUrl}Payment/free-trial`,
      request,
    );
  }

  getRemainingSubscriptionDays(
    remainingSubscriptionDays: RemainingSubscriptionDays,
  ): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}Payment/remaining_subscription_days`,
      remainingSubscriptionDays,
    );
  }
  // isActiveSubscription(
  //   request: IsActiveSubscriptionRequest,
  // ): Observable<IsActiveSubscriptionResponse> {
  //   return this.http.post<IsActiveSubscriptionResponse>(
  //     `${this.apiUrl}Payment/isactive_subscription_plan`,
  //     request,
  //   );
  // }
  checkAndStoreSubscriptionStatus(
    request: IsActiveSubscriptionRequest,
  ): Observable<boolean> {
    return this.isActiveSubscription(request).pipe(
      map((response) => {
        // Extract the actual boolean value from nested data structure
        const isActive = response?.data?.[0]?.isActiveSubscription === 1;

        // Store in localStorage for easy access throughout the app
        localStorage.setItem('isActiveSubscription', JSON.stringify(isActive));

        return isActive;
      }),
      catchError((error) => {
        console.error('Subscription check failed:', error);
        // Default to false if check fails
        localStorage.setItem('isActiveSubscription', 'false');
        return of(false);
      }),
    );
  }

  isActiveSubscription(
    request: IsActiveSubscriptionRequest,
  ): Observable<IsActiveSubscriptionResponse> {
    return this.http.post<IsActiveSubscriptionResponse>(
      `${this.apiUrl}Payment/isactive_subscription_plan`,
      request,
    );
  }
  // checkAndStoreSubscriptionStatus(
  //   request: IsActiveSubscriptionRequest,
  // ): Observable<number> {
  //   if (this.isActiveSubscriptionFlag !== null) {
  //     return new Observable((observer) => {
  //       observer.next(this.isActiveSubscriptionFlag!);
  //       observer.complete();
  //     });
  //   }

  //   return new Observable((observer) => {
  //     this.isActiveSubscription(request).subscribe({
  //       next: (response) => {
  //         this.isActiveSubscriptionFlag =
  //           response.data?.[0]?.isActiveSubscription ?? 0;
  //         observer.next(this.isActiveSubscriptionFlag);
  //         observer.complete();
  //       },
  //       error: (error) => {
  //         this.isActiveSubscriptionFlag = 0;
  //         observer.error(error);
  //       },
  //     });
  //   });
  // }
  getStoredSubscriptionStatus(): string | null {
    return localStorage.getItem('isActiveSubscription');
  }
 /*  isSubscriptionActive(): boolean {
    const activeVenue = this.venueContextService.activeVenue || this.tokenStorage.getActiveVenueContext();
    if (activeVenue && activeVenue.isOrganizationAdmin === false) {
      return true;
    }
    const user = this.tokenStorage.getUser();
    if (user && user.roleId !== 4 && (!user.roles || !user.roles.includes('OrgAdmin'))) {
      return true;
    }
    return localStorage.getItem('isActiveSubscription') === 'true';
  } */

    isSubscriptionActive(): boolean {
      return localStorage.getItem('isActiveSubscription') === 'true';
    }
    
  clearSubscriptionCache(): void {
    this.isActiveSubscriptionFlag = null;
  }
  checkoutStart(
    request: CheckoutStartRequest,
  ): Observable<ApiResponse<CheckoutStartResponse>> {
    return this.http.post<ApiResponse<CheckoutStartResponse>>(
      `${this.apiUrl}Payment/checkout-start`,
      request,
    );
  }
  getPostPayEligibility(
    businessId: number,
  ): Observable<ApiResponse<PostPayEligibilityResponse>> {
    return this.http.get<ApiResponse<PostPayEligibilityResponse>>(
      `${this.apiUrl}post-pay/eligibility/${businessId}`,
    );
  }

  activatePostPay(
    request: ActivatePostPayRequest,
  ): Observable<ApiResponse<ActivatePostPayResponse>> {
    return this.http.post<ApiResponse<ActivatePostPayResponse>>(
      `${this.apiUrl}post-pay/activate`,
      request,
    );
  }
  createCardSession(
    request: CreateCardSessionRequest,
  ): Observable<CreateCardSessionResponse> {
    return this.http.post<CreateCardSessionResponse>(
      `${this.apiUrl}Payment/make-payment`,
      request,
    );
  }

  getSubscriptionPlans(subscriptionPlan: SubscriptionPlan): Observable<any> {
    return this.http.post<any>(
      `${this.apiUrl}Payment/subscriptions-plans`,
      subscriptionPlan,
    );
  }

  createSubscription(
    request: SubscriptionCreateRequest,
  ): Observable<SubscriptionCreateResponse> {
    return this.http.post<SubscriptionCreateResponse>(
      `${this.apiUrl}Subscription/subscribe`,
      request,
    );
  }

  createPayToAgreement(
    request: PayToAgreementRequest,
  ): Observable<PayToAgreementResponse> {
    return this.http.post<PayToAgreementResponse>(
      `${this.apiUrl}Subscription/payto-agreement`,
      request,
    );
  }
  saveBillingAddress(
    request: SaveBillingAddressRequest,
  ): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.apiUrl}Payment/save_billing_address`,
      request,
    );
  }
  paymentSuccess(request: PaymentSuccessRequest): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.apiUrl}Payment/payment_success`,
      request,
    );
  }

  InsertRefferalCode(
    request: PaymentSuccessRequest,
  ): Observable<ApiResponse<any>> {
    return this.http.post<ApiResponse<any>>(
      `${this.apiUrl}Payment/insert_referral_code`,
      request,
    );
  }
  // ============ Transaction Query Methods ============

  /**
   * Get transaction details by reference
   * @param transactionReference The client transaction unique reference
   */
  getTransaction(
    transactionReference: string,
  ): Observable<ApiResponse<MonoovaTransactionResponse>> {
    return this.http.get<ApiResponse<MonoovaTransactionResponse>>(
      `${this.apiUrl}Payment/transaction/${encodeURIComponent(transactionReference)}`,
    );
  }

  /**
   * Get transactions by date range
   * @param createdStartDate Start date for query (ISO format)
   * @param createdEndDate Optional end date
   * @param pageNumber Page number (default 1)
   * @param pageSize Page size (default 50)
   */
  getTransactions(
    createdStartDate: string,
    createdEndDate?: string,
    pageNumber: number = 1,
    pageSize: number = 50,
  ): Observable<ApiResponse<MonoovaTransactionListResponse>> {
    let params = `createdStartDate=${encodeURIComponent(createdStartDate)}`;
    if (createdEndDate) {
      params += `&createdEndDate=${encodeURIComponent(createdEndDate)}`;
    }
    if (pageNumber > 1) {
      params += `&pageNumber=${pageNumber}`;
    }
    if (pageSize !== 50) {
      params += `&pageSize=${pageSize}`;
    }
    return this.http.get<ApiResponse<MonoovaTransactionListResponse>>(
      `${this.apiUrl}Payment/transactions?${params}`,
    );
  }

  // ============ Refund Methods ============

  /**
   * Request a refund for a payment
   * @param request Refund request details
   */
  requestRefund(
    request: RefundRequest,
  ): Observable<ApiResponse<RefundResponse>> {
    return this.http.post<ApiResponse<RefundResponse>>(
      `${this.apiUrl}Payment/refund`,
      request,
    );
  }

  /**
   * Get refund status by unique request ID
   * @param uniqueRequestId The async request ID from the refund request
   */
  getRefundStatus(
    uniqueRequestId: string,
  ): Observable<ApiResponse<MonoovaAsyncStatusResponse>> {
    return this.http.get<ApiResponse<MonoovaAsyncStatusResponse>>(
      `${this.apiUrl}Payment/refund/status/${encodeURIComponent(uniqueRequestId)}`,
    );
  }

  // ============ Payment Token Methods ============

  /**
   * Get saved payment tokens for a customer
   * @param customerId The Monoova customer ID
   * @param tokenReference Optional specific token reference
   */
  getPaymentTokens(
    customerId: string,
    tokenReference?: string,
  ): Observable<ApiResponse<MonoovaTokenDetailsResponse>> {
    let url = `${this.apiUrl}Payment/tokens?customerId=${encodeURIComponent(customerId)}`;
    if (tokenReference) {
      url += `&tokenReference=${encodeURIComponent(tokenReference)}`;
    }
    return this.http.get<ApiResponse<MonoovaTokenDetailsResponse>>(url);
  }

  /**
   * Update payment token status
   * @param customerId The Monoova customer ID
   * @param tokenReference The token reference
   * @param status New status: Active, Paused, or Deleted
   */
  updatePaymentTokenStatus(
    customerId: string,
    tokenReference: string,
    status: 'Active' | 'Paused' | 'Deleted',
  ): Observable<ApiResponse<any>> {
    return this.http.patch<ApiResponse<any>>(
      `${this.apiUrl}Payment/tokens/${encodeURIComponent(customerId)}/${encodeURIComponent(tokenReference)}`,
      { status },
    );
  }

  /**
   * Delete a payment token (sets status to Deleted)
   * @param customerId The Monoova customer ID
   * @param tokenReference The token reference
   */
  deletePaymentToken(
    customerId: string,
    tokenReference: string,
  ): Observable<ApiResponse<any>> {
    return this.updatePaymentTokenStatus(customerId, tokenReference, 'Deleted');
  }

  /**
   * Pause a payment token
   * @param customerId The Monoova customer ID
   * @param tokenReference The token reference
   */
  pausePaymentToken(
    customerId: string,
    tokenReference: string,
  ): Observable<ApiResponse<any>> {
    return this.updatePaymentTokenStatus(customerId, tokenReference, 'Paused');
  }

  /**
   * Reactivate a paused payment token
   * @param customerId The Monoova customer ID
   * @param tokenReference The token reference
   */
  activatePaymentToken(
    customerId: string,
    tokenReference: string,
  ): Observable<ApiResponse<any>> {
    return this.updatePaymentTokenStatus(customerId, tokenReference, 'Active');
  }

  validateBsb(bsb: string): boolean {
    const cleanBsb = bsb.replace(/[\s-]/g, '');
    return /^\d{6}$/.test(cleanBsb);
  }

  validateAccountNumber(accountNumber: string): boolean {
    const cleanAccount = accountNumber.replace(/\s/g, '');
    return /^\d{6,9}$/.test(cleanAccount);
  }

  formatBsb(bsb: string): string {
    const cleanBsb = bsb.replace(/[\s-]/g, '');
    if (cleanBsb.length === 6) {
      return `${cleanBsb.substring(0, 3)}-${cleanBsb.substring(3)}`;
    }
    return bsb;
  }
  getCancellationPolicy(): Observable<
    ApiResponse<{ Title: string; CancellationPolicy: string }>
  > {
    return this.http.get<
      ApiResponse<{ Title: string; CancellationPolicy: string }>
    >(`${this.apiUrl}Business/get_showlab_cancellation_policy`);
  }
showcaseCreatorCompleted(request: any): Observable<ApiResponse<ShowcaseCreatorCompletedResponse>> {
  const headers = new HttpHeaders({
    'Content-Type': 'application/json'
  });
  
  return this.http.post<ApiResponse<ShowcaseCreatorCompletedResponse>>(
    `${this.apiUrl}Payment/showcase-creator-completed`,
    request,
    { headers }
  );
}

getBusinessAddressByUserId(userId: number): Observable<ApiResponse<BusinessAddressResponse>> {
  return this.http.get<ApiResponse<BusinessAddressResponse>>(
    `${this.apiUrl}Business/get_business_address/${userId}`
  );
}

updatePaymentDetailsStart(request: { BusinessId: number; UserId: number; RoleId: number; CurrencyCode?: string }): Observable<ApiResponse<{ paymentId: number; correlationId: string; cardLastFour?: string }>> {
  return this.http.post<ApiResponse<{ paymentId: number; correlationId: string; cardLastFour?: string }>>(
    `${this.apiUrl}Payment/update-payment-details-start`,
    request
  );
}

updatePaymentDetailsSuccess(request: { UserId: number; RoleId: number; BusinessId: number; CorrelationId: string; ClientPaymentTokenUniqueReference: string; ClientTransactionUniqueReference: string }): Observable<ApiResponse<any>> {
  return this.http.post<ApiResponse<any>>(
    `${this.apiUrl}Payment/update-payment-details-success`,
    request
  );
}

cardUpdateStart(
  request: CardUpdateStartRequest,
): Observable<ApiResponse<CardUpdateStartResponse>> {
  return this.http.post<ApiResponse<CardUpdateStartResponse>>(
    `${this.apiUrl}Payment/card-update-start`,
    request,
  );
}
 
cardUpdateComplete(
  request: CardUpdateCompleteRequest,
): Observable<ApiResponse<any>> {
  return this.http.post<ApiResponse<any>>(
    `${this.apiUrl}Payment/card-update-complete`,
    request,
  );
}
}
